import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';

export type ThemePreference = 'light' | 'dark' | 'auto';

/** The preference with "auto" already resolved — what is actually on screen. */
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Drives the theme by stamping `data-theme` on <html>.
 * `_base.scss` maps that attribute to `color-scheme`, which both
 * Angular Material (theme-type: color-scheme) and our own
 * light-dark() tokens respond to. With no attribute ("auto"),
 * the app follows the operating system preference.
 *
 * CSS resolves "auto" on its own through `color-scheme: light dark`,
 * but TypeScript can't read that, so anything that has to branch on the
 * theme in code (picking a logo file, reading chart colors) reads
 * `resolvedTheme`/`isDarkMode` here rather than `theme` — otherwise
 * "auto" would silently behave as light.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly theme = signal<ThemePreference>(readStoredPreference());

  /** Live OS preference: "auto" has to keep up when the system flips. */
  private readonly prefersDark = signal(matchDarkQuery()?.matches ?? false);

  readonly resolvedTheme = computed<ResolvedTheme>(() => {
    const preference = this.theme();
    if (preference !== 'auto') {
      return preference;
    }
    return this.prefersDark() ? 'dark' : 'light';
  });

  readonly isDarkMode = computed(() => this.resolvedTheme() === 'dark');

  constructor() {
    effect(() => {
      const preference = this.theme();
      if (preference === 'auto') {
        document.documentElement.removeAttribute('data-theme');
        localStorage.removeItem(STORAGE_KEY);
      } else {
        document.documentElement.setAttribute('data-theme', preference);
        localStorage.setItem(STORAGE_KEY, preference);
      }
    });

    const query = matchDarkQuery();
    if (query) {
      const onChange = (event: MediaQueryListEvent) => this.prefersDark.set(event.matches);
      query.addEventListener('change', onChange);
      inject(DestroyRef).onDestroy(() => query.removeEventListener('change', onChange));
    }
  }

  setTheme(preference: ThemePreference = 'auto'): void {
    this.theme.set(preference);
  }
}

function readStoredPreference(): ThemePreference {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'auto';
}

/** Absent under SSR and in test environments that don't implement matchMedia. */
function matchDarkQuery(): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY)
    : null;
}
